import { BookOpen, Briefcase, History, Plug, Users, WalletCards, type LucideIcon } from 'lucide-react'

export interface SettingsItem {
  to: string
  label: string
  // Prefixos que também acendem o item (fichas e endereços antigos). O próprio `to` sempre vale.
  match?: string[]
  // Pendência que aparece como número ao lado do item (e da seção), como os avisos da Meta.
  badge?: 'notion-unmatched'
}

export interface SettingsSection {
  id: string
  label: string
  icon: LucideIcon
  items: SettingsItem[]
}

// A escada das Configurações (seção → item), no modelo das configurações da Meta. Tudo o que é cadastro ou regra
// mora aqui; o menu principal fica só com o trabalho do dia a dia.
export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: 'pessoas',
    label: 'Pessoas e empresas',
    icon: Users,
    items: [
      { to: '/equipe', label: 'Equipe' },
      { to: '/profissionais', label: 'Profissionais', match: ['/medicos'] },
      { to: '/fornecedores', label: 'Fornecedores' },
      { to: '/config/portal-parceiro', label: 'Portal do Parceiro' },
    ],
  },
  {
    id: 'catalogo',
    label: 'Catálogo',
    icon: BookOpen,
    items: [
      { to: '/config/procedimentos', label: 'Procedimentos' },
      { to: '/config/pacotes', label: 'Pacotes' },
      { to: '/config/servicos-proprios', label: 'Serviços da clínica' },
      { to: '/config/requisitos', label: 'Requisitos' },
      { to: '/config/precos-procedimentos', label: 'Preços por procedimento' },
      { to: '/config/tabelas-custo', label: 'Tabelas de custo' },
      { to: '/config/tabelas-precos', label: 'Tabelas de venda (legado)' },
    ],
  },
  {
    id: 'comercial',
    label: 'Comercial',
    icon: Briefcase,
    items: [
      { to: '/config/estagios', label: 'Estágios do pipeline' },
      { to: '/config/cadencias', label: 'Cadências' },
      { to: '/config/motivos', label: 'Motivos' },
    ],
  },
  {
    id: 'financeiro',
    label: 'Financeiro',
    icon: WalletCards,
    items: [
      { to: '/config/regras-repasse', label: 'Regras de repasse' },
      { to: '/config/metodos-pagamento', label: 'Métodos de pagamento' },
      { to: '/config/contas-bancarias', label: 'Contas bancárias' },
    ],
  },
  {
    id: 'integracoes',
    label: 'Integrações',
    icon: Plug,
    items: [
      { to: '/config/integracoes', label: 'Status das integrações', badge: 'notion-unmatched' },
      { to: '/config/tags-notion', label: 'Tags do Notion' },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: History,
    items: [{ to: '/logs', label: 'Logs' }],
  },
]

export const SETTINGS_HOME = '/configuracoes'

const underPath = (pathname: string, base: string) => pathname === base || pathname.startsWith(base + '/')

/** O item das Configurações dono da URL: o prefixo mais longo vence (ex.: /config/procedimentos/12 → Procedimentos). */
export function settingsItemFor(pathname: string): { section: SettingsSection; item: SettingsItem } | null {
  let best: { section: SettingsSection; item: SettingsItem; length: number } | null = null
  for (const section of SETTINGS_SECTIONS) {
    for (const item of section.items) {
      for (const base of [item.to, ...(item.match ?? [])]) {
        if (underPath(pathname, base) && (!best || base.length > best.length)) {
          best = { section, item, length: base.length }
        }
      }
    }
  }
  return best && { section: best.section, item: best.item }
}

/** A URL está dentro das Configurações (home, um item ou um endereço antigo de /config). */
export function isSettingsPath(pathname: string): boolean {
  return underPath(pathname, SETTINGS_HOME) || underPath(pathname, '/config') || settingsItemFor(pathname) !== null
}
